import { fetchWithCSRF } from "@/helpers/common/csrf";
import clsx from "clsx";
import { ChevronLeft, ChevronRight, RefreshCw, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { ChatLogPagination, ChatMessage } from "./_types";

const API_URL = import.meta.env.VITE_VANDERHEIM_NINJA_API_URL;
const SEARCH_DELAY_MS = 300;

const emptyFilters = {
    search: "",
    chatter: "",
    role: "",
    dateFrom: "",
    dateTo: "",
    order: "desc",
    commandsOnly: false,
};

const inputClasses =
    "rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100";

const pageButtonClasses = (enabled: boolean) =>
    clsx(
        "flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
        enabled
            ? "bg-slate-200 text-slate-700 hover:bg-slate-300 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600"
            : "cursor-not-allowed bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-600",
    );

function roleBadges(message: ChatMessage) {
    return [
        message.is_broadcaster && "Broadcaster",
        message.is_moderator && "Mod",
        message.is_vip && "VIP",
        message.is_subscriber && "Sub",
    ].filter(Boolean) as string[];
}

export default function ChatLogsPage() {
    const [filters, setFilters] = useState(emptyFilters);
    const [typed, setTyped] = useState({ search: "", chatter: "" });
    const [page, setPage] = useState(1);
    const [refreshCount, setRefreshCount] = useState(0);
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [pagination, setPagination] = useState<ChatLogPagination | null>(
        null,
    );
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);

    const updateFilters = (changes: Partial<typeof emptyFilters>) => {
        setFilters((current) => ({ ...current, ...changes }));
        setPage(1);
    };

    // Wait for a pause in typing so each keystroke doesn't run a search.
    useEffect(() => {
        const timer = setTimeout(() => {
            setFilters((current) =>
                current.search === typed.search &&
                current.chatter === typed.chatter
                    ? current
                    : { ...current, ...typed },
            );
            setPage((current) =>
                filters.search === typed.search &&
                filters.chatter === typed.chatter
                    ? current
                    : 1,
            );
        }, SEARCH_DELAY_MS);
        return () => clearTimeout(timer);
    }, [typed, filters.search, filters.chatter]);

    useEffect(() => {
        const controller = new AbortController();
        const params = new URLSearchParams({
            page: String(page),
            page_size: "50",
            order: filters.order,
        });
        if (filters.search) params.set("search", filters.search);
        if (filters.chatter) params.set("chatter", filters.chatter);
        if (filters.role) params.set("role", filters.role);
        if (filters.commandsOnly) params.set("commands_only", "true");
        if (filters.dateFrom) {
            params.set("date_from", new Date(filters.dateFrom).toISOString());
        }
        if (filters.dateTo) {
            params.set("date_to", new Date(filters.dateTo).toISOString());
        }

        setLoading(true);
        setFailed(false);
        fetchWithCSRF(`${API_URL}/longhouse-api/village/chat-logs/?${params}`, {
            signal: controller.signal,
        })
            .then(async (res) => {
                if (!res.ok) throw new Error(`Status ${res.status}`);
                const data = await res.json();
                setMessages(data.results);
                setPagination(data.pagination);
                setLoading(false);
            })
            .catch((error) => {
                if (controller.signal.aborted) return;
                console.error("Error fetching chat logs:", error);
                setFailed(true);
                setLoading(false);
            });

        return () => controller.abort();
    }, [filters, page, refreshCount]);

    const clearFilters = () => {
        setTyped({ search: "", chatter: "" });
        updateFilters(emptyFilters);
    };

    return (
        <div className="px-4 py-6 sm:px-6 lg:px-8">
            <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                        Chat Logs
                    </h1>
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                        Messages from the main Twitch channel.
                    </p>
                </div>
                <button
                    onClick={() => setRefreshCount((count) => count + 1)}
                    className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-700"
                >
                    <RefreshCw className="h-4 w-4" />
                    Refresh
                </button>
            </div>

            <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <label className="relative">
                    <span className="sr-only">Search messages</span>
                    <Search className="absolute top-2.5 left-3 h-4 w-4 text-slate-400" />
                    <input
                        type="search"
                        placeholder="Search messages"
                        value={typed.search}
                        onChange={(e) =>
                            setTyped({ ...typed, search: e.target.value })
                        }
                        className={clsx(inputClasses, "w-full pl-9")}
                    />
                </label>
                <label>
                    <span className="sr-only">Filter by chatter</span>
                    <input
                        type="search"
                        placeholder="Chatter"
                        value={typed.chatter}
                        onChange={(e) =>
                            setTyped({ ...typed, chatter: e.target.value })
                        }
                        className={clsx(inputClasses, "w-full")}
                    />
                </label>
                <label>
                    <span className="sr-only">Filter by role</span>
                    <select
                        value={filters.role}
                        onChange={(e) =>
                            updateFilters({ role: e.target.value })
                        }
                        className={clsx(inputClasses, "w-full")}
                    >
                        <option value="">All roles</option>
                        <option value="broadcaster">Broadcaster</option>
                        <option value="moderator">Moderators</option>
                        <option value="vip">VIPs</option>
                        <option value="subscriber">Subscribers</option>
                    </select>
                </label>
                <label>
                    <span className="sr-only">Sort order</span>
                    <select
                        value={filters.order}
                        onChange={(e) =>
                            updateFilters({ order: e.target.value })
                        }
                        className={clsx(inputClasses, "w-full")}
                    >
                        <option value="desc">Newest first</option>
                        <option value="asc">Oldest first</option>
                    </select>
                </label>
                <label className="text-xs text-slate-600 dark:text-slate-400">
                    From
                    <input
                        type="datetime-local"
                        value={filters.dateFrom}
                        onChange={(e) =>
                            updateFilters({ dateFrom: e.target.value })
                        }
                        className={clsx(inputClasses, "mt-1 w-full")}
                    />
                </label>
                <label className="text-xs text-slate-600 dark:text-slate-400">
                    To
                    <input
                        type="datetime-local"
                        value={filters.dateTo}
                        onChange={(e) =>
                            updateFilters({ dateTo: e.target.value })
                        }
                        className={clsx(inputClasses, "mt-1 w-full")}
                    />
                </label>
                <label className="flex items-center gap-2 self-end py-2 text-sm text-slate-700 dark:text-slate-300">
                    <input
                        type="checkbox"
                        checked={filters.commandsOnly}
                        onChange={(e) =>
                            updateFilters({ commandsOnly: e.target.checked })
                        }
                    />
                    ? commands only
                </label>
                <button
                    onClick={clearFilters}
                    className="self-end rounded-lg bg-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-300 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600"
                >
                    Clear filters
                </button>
            </div>

            <div className="overflow-hidden rounded-xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800">
                {failed ? (
                    <p className="p-8 text-center text-sm text-red-600 dark:text-red-400">
                        Chat logs could not be loaded.
                    </p>
                ) : messages.length === 0 ? (
                    <p className="p-8 text-center text-sm text-slate-600 dark:text-slate-400">
                        {loading ? "Loading…" : "No messages found."}
                    </p>
                ) : (
                    <ul
                        className={clsx(
                            "divide-y divide-slate-200 dark:divide-slate-700",
                            loading && "opacity-60",
                        )}
                    >
                        {messages.map((message) => (
                            <li
                                key={message.id}
                                className="flex flex-col gap-1 px-4 py-3 sm:flex-row sm:gap-4"
                            >
                                <time
                                    dateTime={message.sent_at}
                                    className="shrink-0 font-mono text-xs text-slate-500 sm:w-44 sm:pt-0.5 dark:text-slate-400"
                                >
                                    {new Date(message.sent_at).toLocaleString()}
                                </time>
                                <div className="min-w-0 text-sm">
                                    <button
                                        onClick={() =>
                                            setTyped({
                                                ...typed,
                                                chatter: message.chatter_login,
                                            })
                                        }
                                        title="Show this chatter's messages"
                                        className="mr-2 font-semibold text-slate-900 hover:underline dark:text-slate-100"
                                    >
                                        {message.chatter_name}
                                    </button>
                                    {roleBadges(message).map((badge) => (
                                        <span
                                            key={badge}
                                            className="mr-1 rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-700 dark:bg-slate-700 dark:text-slate-300"
                                        >
                                            {badge}
                                        </span>
                                    ))}
                                    {message.source_broadcaster_login && (
                                        <span className="mr-1 rounded bg-purple-100 px-1.5 py-0.5 text-xs text-purple-800 dark:bg-purple-900 dark:text-purple-300">
                                            via{" "}
                                            {message.source_broadcaster_login}
                                        </span>
                                    )}
                                    <p className="mt-1 break-words whitespace-pre-wrap text-slate-800 dark:text-slate-200">
                                        {message.text}
                                    </p>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}

                {pagination && pagination.total_items > 0 && (
                    <div className="flex items-center justify-between gap-3 border-t border-slate-200 px-4 py-3 dark:border-slate-700">
                        <p className="text-sm text-slate-600 dark:text-slate-400">
                            Page {pagination.page} of {pagination.total_pages} (
                            {pagination.total_items} messages)
                        </p>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setPage(page - 1)}
                                disabled={!pagination.has_previous_page}
                                className={pageButtonClasses(
                                    pagination.has_previous_page,
                                )}
                            >
                                <ChevronLeft className="h-4 w-4" />
                                Previous
                            </button>
                            <button
                                onClick={() => setPage(page + 1)}
                                disabled={!pagination.has_next_page}
                                className={pageButtonClasses(
                                    pagination.has_next_page,
                                )}
                            >
                                Next
                                <ChevronRight className="h-4 w-4" />
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
